import { DateTime } from '@ksyos/date-time';
import { Form, FormGroup, FormParagraph } from '@ksyos/design-system';
import { useState } from 'react';
import type { IOption } from '@frontend/components/interfaces/option';
import type { ValidationErrors } from '@frontend/utils/validation';
import { AccountRequestStatus } from '@common/enums/accountRequestStatus';
import { getDisplayName } from '@common/utils/getDisplayName';
import { accountRequestUpdateApproverSchema } from '@common/validation/accountRequest';
import { getSelectedLanguage } from '@frontend/translate';
import { hasAccountRequestAuthorization } from '@frontend/utils/hasAuthorization';
import { useAccountRequestContext } from '@frontend/utils/hooks/useAccountRequestContext';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';
import { AccountRequestSelectApprover } from '../AccountRequestSelectApprover';
import { AccountRequestEditingButtons } from './AccountRequestEditingButtons';
import { AccountRequestReviewButtons } from './AccountRequestReviewButtons';
import { SelectMatchingPractitionerTable } from './SelectMatchingPractitionerTable';

export function AccountRequestForm() {
    const { accountRequest, approvers, setIsEditing, updateApprover, needsKsyosApproval, isEditing } =
        useAccountRequestContext();
    const language = getSelectedLanguage();

    const hasReviewAuthorization = hasAccountRequestAuthorization('review', true);

    const [selectedApproverId, setSelectedApproverId] = useState<string | null>(accountRequest.approverPractitionerId);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const showReviewButtons =
        !isEditing && accountRequest.status === AccountRequestStatus.pending && hasReviewAuthorization;

    return (
        <Form submitOnEnter={false}>
            {renderValue({
                label: language.accountRequest.profession,
                value: language.professionEnum[accountRequest.userData.employment.profession],
            })}
            {renderValue({
                label: language.accountRequest.identifierDeclaration,
                value: accountRequest.userData.practitioner.identifierDeclaration,
            })}
            {renderValue({
                label: language.practitioner.identifierRegistrationKp,
                value: accountRequest.userData.practitioner.identifierRegistrationKp,
            })}
            {renderValue({
                label: language.accountRequest.zorgMailNumber,
                value: accountRequest.userData.employment.zorgmailNummer,
            })}
            {renderValue({
                label: language.accountRequest.nameGiven,
                value: accountRequest.userData.practitioner.nameGiven,
            })}
            {renderValue({
                label: language.accountRequest.nameInfix,
                value: accountRequest.userData.practitioner.nameInfix,
            })}
            {renderValue({
                label: language.accountRequest.nameFamily,
                value: accountRequest.userData.practitioner.nameFamily,
            })}
            {renderValue({
                label: language.accountRequest.gender,
                value: language.genderEnum[accountRequest.userData.practitioner.gender],
            })}
            {renderValue({
                label: language.accountRequest.workEmail,
                value: accountRequest.userData.practitioner.emailAddress,
            })}
            {renderValue({
                label: language.accountRequest.startDate,
                value: DateTime.from(accountRequest.userData.employment.startDate).format('DD-MM-YYYY'),
            })}
            {isEditing ? (
                <AccountRequestSelectApprover
                    approverOptions={approvers.map(
                        ({ displayName, id }): IOption<string> => ({
                            label: displayName,
                            value: id,
                        })
                    )}
                    errors={errors}
                    label={language.accountRequest.approver}
                    onChange={setSelectedApproverId}
                    organizationId={accountRequest.organizationId}
                    selectedApproverId={selectedApproverId}
                />
            ) : (
                renderValue({
                    label: language.accountRequest.approver,
                    value: accountRequest.approverPractitionerId
                        ? getDisplayName(accountRequest.approverPractitioner)
                        : null,
                })
            )}
            {renderValue({
                label: language.accountRequest.reviewedBy,
                value: accountRequest.reviewedByPractitionerId
                    ? getDisplayName(accountRequest.reviewedByPractitioner)
                    : null,
            })}
            {renderValue({
                label: language.accountRequest.requestedBy,
                value: getDisplayName(accountRequest.requestedByPractitioner),
            })}
            {renderValue({
                label: language.accountRequest.rejectedAt,
                value: accountRequest.rejectedAt ? DateTime.from(accountRequest.rejectedAt).format('DD-MM-YYYY') : null,
            })}
            {renderValue({
                label: language.accountRequest.rejectionReason,
                value: accountRequest.rejectionReason,
            })}
            {accountRequest.status === AccountRequestStatus.pending && accountRequest.hasMatchedOnNameAndGender && (
                <SelectMatchingPractitionerTable />
            )}
            {isEditing && (
                <AccountRequestEditingButtons
                    onCancel={handleCancel}
                    onSave={() => handleSave(selectedApproverId)}
                />
            )}
            {showReviewButtons && <AccountRequestReviewButtons />}
        </Form>
    );

    function handleCancel() {
        setIsEditing(false);
        setErrors(emptyValidationErrors);
        setSelectedApproverId(accountRequest.approverPractitionerId);
    }

    async function handleSave(newApproverId: string | null) {
        if (newApproverId !== accountRequest.approverPractitionerId) {
            const { errors: validationErrors } = validate(accountRequestUpdateApproverSchema, {
                approverPractitionerId: newApproverId,
                needsKsyosApproval,
            });

            if (validationErrors.isEmpty()) {
                await updateApprover(newApproverId);
            } else {
                setErrors(validationErrors);
            }
        }

        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }
}

function renderValue({ label, value }: { label: string; value: string | null }) {
    if (!value) {
        return null;
    }

    return (
        <FormGroup label={label}>
            <FormParagraph>{value}</FormParagraph>
        </FormGroup>
    );
}
